// @givmo/app-charity — the back-office enrollment operations, addressed by URL (CBO-IN3).
//
// ══ WHY THIS FILE IS HAND-WRITTEN ══════════════════════════════════════════════════════════════
//
// The enrollment surface landed on the backend AFTER the committed OpenAPI snapshot was last
// refreshed, so there is no generated SDK operation, no generated type and no TanStack Query option
// factory for any of the six routes below.
//
// `agent_docs/api-client.md` § *A whole ROUTE the snapshot lacks* is the sanctioned disposition, and
// it is deliberately NOT "refresh the snapshot for one feature": a full refresh absorbs every
// unrelated backend change since the last regen (that doc carries a whole section on reviewing a
// refresh for collateral breaks) and collides with any regen already in flight. `givmoClient` is the same
// `@hey-api/client-fetch` client the generated operations bind to, so a call made here inherits the
// entire interceptor stack — Bearer, App Check, `X-Request-Id` (`ch-` on this app), the
// `{data,meta,links}` envelope unwrap, and the `{errors:[…]}` → `GivmoError` mapping. Nothing about
// the request differs from a generated one except who wrote the URL.
//
// The response types are not in `types.gen.ts` either, so every read resolves to `unknown` and is
// handed to `enrollment-decode.ts`. That is what keeps a shape this app guessed wrong from becoming
// a crash.
//
// ══ GRADUATION, WHEN A REGEN ABSORBS THESE ROUTES ══════════════════════════════════════════════
//
// This module and its URL test go — the URL, query string and cache key are generated then, and a
// test of URL-building this repo no longer does proves nothing. Two things do NOT go with them, and
// one thing changes silently:
//
//   • `enrollment-decode.ts` STAYS, moved into each query's `select` so the cache entry keeps the
//     generated shape. A generated type describes the wire, not what arrived.
//   • The screens' own request decisions stay pinned — asserted then on what a page ASKS for rather
//     than on a URL string.
//   • ⚠ The swap moves these queries INTO family-invalidation reach. The hand-written keys carry no
//     `_id`, so a `createQueryKey`-prefix predicate never matched them; a generated key does. Decide
//     whether that refetching is wanted rather than assuming the graduation is behaviour-neutral.
//
// ══ TWO CONTRACT FACTS THAT SHAPE THIS FILE ════════════════════════════════════════════════════
//
//   • The charity surface deliberately carries NONE of the platform-fact fields the operator models
//     add: the permanent ledger identity, the target environment, the two CBO-AC6 seam references,
//     the provisioning artifact's id, the creating user, and the second-home copy flag. A backend
//     test asserts their absence from these responses. Nothing here asks for them and nothing
//     downstream may expect them.
//   • `POST …/submission` answers `submitted` OR `approved`. Approval fires automatically the moment
//     the precondition set is satisfied and does not when it is not, so a caller that treated
//     submission as approval would tell an organization its books exist when nothing was created. It
//     is also state-based idempotent: a replay returns the current state and records no second
//     event, which is why the screens can retry it without a guard.
//
// ══ URL BUILDING ═══════════════════════════════════════════════════════════════════════════════
//
// Every interpolated segment is an id that came from the backend, and every one is
// `encodeURIComponent`-ed anyway. Not because a UUID needs escaping, but because the day one of
// these segments stops being a UUID is not the day anybody re-reads this file.

import { givmoClient } from '@givmo/api-client';

const ENROLLMENT = '/charity-admin/enrollment';

/**
 * Pagination is `page[limit]` / `page[offset]`, NOT `limit` / `offset` — the repo-wide
 * `QUERY_LIMIT_NAME` / `QUERY_OFFSET_NAME` convention. `page[limit]` is 1..200, `page[offset]` is
 * `ge=0`, and the gate answers before query validation runs, so a dark module 404s rather than 422s
 * an out-of-range value.
 */
const LIMIT_PARAM = 'page[limit]';
const OFFSET_PARAM = 'page[offset]';

/**
 * The page size the console requests.
 *
 * ⚠ This surface reads the FIRST page and makes no completeness claim, which is a different posture
 * from the compliance register's exhaustive walk — and the difference is structural rather than a
 * shortcut. The listing is ordered NEWEST FIRST, and an organization holds at most one live record
 * at a time (the backend answers 409 to a second one). So the record this console acts on is on page
 * one by construction, whatever the length of the history behind it. Nothing rendered says "this is
 * every record", because the client has not read every record.
 */
export const ENROLLMENT_PAGE_SIZE = 50;

/**
 * `…/charities/{id}/enrollments` — the scope every charity-side enrollment call lives under.
 *
 * Exported for `enrollment-backfill-transport.ts`, which addresses the intake doors hanging off one
 * record inside this same scope. Shared rather than restated there: a second copy of the prefix is a
 * second thing to update the day the server path moves, and the one that is not updated fails as a
 * 404 the console renders as "not available here" — the most misleading error this feature has.
 */
export function charityBase(charityId: string): string {
  return `${ENROLLMENT}/charities/${encodeURIComponent(charityId)}/enrollments`;
}

/** Append a query string, omitting every absent value so no empty parameter is ever transmitted. */
function withQuery(url: string, params: Record<string, number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query.length === 0 ? url : `${url}?${query}`;
}

/** A page request against the listing. */
export interface EnrollmentPage {
  limit?: number;
  offset?: number;
}

/**
 * The organization's enrollment records, newest first.
 *
 * The response is the canonical `{data, meta, links}` envelope on the wire, which the api-client
 * response middleware REWRITES to the bare `data` array before this code sees it (shape-gated on
 * `meta`/`links`). `meta.total_items` does not survive that rewrite, so no caller here can count the
 * whole set — which is the other half of why this surface makes no completeness claim. The decode
 * reads both shapes anyway, so the screen is correct whether or not the middleware unwrapped.
 */
export async function listEnrollments(charityId: string, page?: EnrollmentPage): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: withQuery(charityBase(charityId), {
      [LIMIT_PARAM]: page?.limit ?? ENROLLMENT_PAGE_SIZE,
      [OFFSET_PARAM]: page?.offset,
    }),
    throwOnError: true,
  });
  return data;
}

/** One record with its event log, oldest event first. */
export async function getEnrollment(charityId: string, enrollmentId: string): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${charityBase(charityId)}/${encodeURIComponent(enrollmentId)}`,
    throwOnError: true,
  });
  return data;
}

/**
 * The create body.
 *
 * `legal_entity_name` is optional here and required nowhere on the charity path — a draft is allowed
 * to be empty, because the organization starting one has not necessarily looked up how its own legal
 * name is registered yet. The request model is `extra="forbid"` server-side, so an unknown key is a
 * 422 once the surface is live; this type is the compile-time version of that.
 */
export interface EnrollmentDraftCreateBody {
  legal_entity_name?: string | null;
  fiscal_year_end_month?: number | null;
}

/** Start a draft record for this organization. Answers 201 with the record and its first event. */
export async function createEnrollmentDraft(
  charityId: string,
  body: EnrollmentDraftCreateBody = {},
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: charityBase(charityId),
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * The draft-edit body.
 *
 * ⚠ ABSENT AND NULL MEAN DIFFERENT THINGS, and the difference is the whole reason this type uses
 * optional keys rather than a full record. Only keys actually PRESENT in the body are applied:
 * omitting a field preserves whatever the record holds, and an explicit `null` clears it. A caller
 * that serialized its whole form on every save would therefore overwrite a field it never showed the
 * user. A body carrying no editable key at all is a 400 rather than a no-op, so the screens only
 * fire this when something changed.
 */
export interface EnrollmentDraftUpdateBody {
  legal_entity_name?: string | null;
  legal_entity_ein?: string | null;
  legal_entity_jurisdiction?: string | null;
  /**
   * The month the organization's financial year ends in, 1–12.
   *
   * ⚠ THE LAST MOMENT IT CAN BE CHOSEN IS BEFORE SUBMISSION, and that is a fact about the books
   * rather than a form-ordering preference. Approval pins it into the payload the organization's
   * books are opened under, and it decides what a year-end and every annual figure MEAN on them —
   * so changing it afterwards is not an edit, it is a different set of books. Left unset it settles
   * to December at submission, which is what the books would have been opened under anyway.
   */
  fiscal_year_end_month?: number | null;
}

/** Edit a draft's recorded details. */
export async function updateEnrollmentDraft(
  charityId: string,
  enrollmentId: string,
  body: EnrollmentDraftUpdateBody,
): Promise<unknown> {
  const { data } = await givmoClient.patch({
    url: `${charityBase(charityId)}/${encodeURIComponent(enrollmentId)}`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Submit the record — the organization's own act of asking for its books.
 *
 * ⚠ Answers `submitted` OR `approved`; render the RETURNED status. There is no request body: what is
 * being submitted is the record as it already stands, and a body would invite a caller to change the
 * details in the same act that freezes them.
 */
export async function submitEnrollment(charityId: string, enrollmentId: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${charityBase(charityId)}/${encodeURIComponent(enrollmentId)}/submission`,
    throwOnError: true,
  });
  return data;
}

/**
 * The withdrawal body. `reason` is optional and recorded on the record as its disposition note.
 *
 * ⚠ Withdrawal is refused (400) once a record is approved, and that refusal is deliberate rather
 * than an ordering quirk: by approval the record carries a permanent ledger identity that a committed
 * store row may already be using, and the store never reuses one. The screens offer the affordance
 * only where the state machine allows it, so the refusal is not something a person has to discover.
 */
export interface EnrollmentWithdrawBody {
  reason?: string | null;
}

/** Withdraw a draft or submitted record. */
export async function withdrawEnrollment(
  charityId: string,
  enrollmentId: string,
  body: EnrollmentWithdrawBody = {},
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${charityBase(charityId)}/${encodeURIComponent(enrollmentId)}/withdrawal`,
    body,
    throwOnError: true,
  });
  return data;
}
